package br.com.texeratx.boardgametimeapp.ui.screens.active_match

import br.com.texeratx.boardgametimeapp.core.active_match.ActiveMatchController
import br.com.texeratx.boardgametimeapp.core.i18n.LanguageManager
import br.com.texeratx.boardgametimeapp.core.spotify.repository.SpotifyPlayerRepository
import br.com.texeratx.boardgametimeapp.data.repository.ExpansionRepository
import br.com.texeratx.boardgametimeapp.data.repository.GameRepository
import br.com.texeratx.boardgametimeapp.data.repository.PlayerRepository
import cafe.adriel.voyager.core.model.ScreenModel
import cafe.adriel.voyager.core.model.screenModelScope
import cafe.adriel.voyager.navigator.Navigator
import io.github.aakira.napier.Napier
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

class StartMatchSetupViewModel(
    private val gameRepository: GameRepository,
    private val playerRepository: PlayerRepository,
    private val expansionRepository: ExpansionRepository,
    private val activeMatchController: ActiveMatchController,
    private val spotifyPlayerRepository: SpotifyPlayerRepository?,
    private val lang: LanguageManager
    ) : ScreenModel {

    private val _state = MutableStateFlow(StartMatchSetupUiState())
    val state = _state.asStateFlow()

    /**
     * Carrega jogos e jogadores (uma vez ao abrir a tela).
     */
    fun load() {
        if (_state.value.games.isNotEmpty() && _state.value.players.isNotEmpty()) return

        screenModelScope.launch {
            _state.value = _state.value.copy(isLoading = true, error = null)

            val gamesResult = gameRepository.getGames()
            val playersResult = playerRepository.getPlayers()

            val games = gamesResult.getOrElse {
                _state.value = _state.value.copy(
                    isLoading = false,
                    error = it.message ?: lang.strings.value.errorLoadingGames
                )
                return@launch
            }

            val players = playersResult.getOrElse {
                _state.value = _state.value.copy(
                    isLoading = false,
                    error = it.message ?: lang.strings.value.errorLoadingPlayers
                )
                return@launch
            }

            _state.value = _state.value.copy(
                isLoading = false,
                games = games,
                players = players,
                error = null
            )
        }
    }

    fun selectGame(gameId: Long) {
        _state.value = _state.value.copy(
            selectedGameId = gameId,
            expansions = emptyList(),
            selectedExpansionIds = emptySet(),
            error = null
        )

        // Ao escolher jogo, carrega expansões desse jogo
        screenModelScope.launch {
            _state.value = _state.value.copy(isLoading = true)

            val result = expansionRepository.getExpansionsByGame(gameId)
            result.fold(
                onSuccess = { list ->
                    _state.value = _state.value.copy(
                        isLoading = false,
                        expansions = list,
                        error = null
                    )
                },
                onFailure = { e ->
                    _state.value = _state.value.copy(
                        isLoading = false,
                        expansions = emptyList(),
                        error = e.message ?: lang.strings.value.errorLoadingExpansions
                    )
                }
            )
        }
    }

    fun togglePlayer(playerId: Long) {
        val current = _state.value.selectedPlayerIds.toMutableSet()
        if (current.contains(playerId)) current.remove(playerId) else current.add(playerId)
        _state.value = _state.value.copy(selectedPlayerIds = current)
    }

    fun toggleExpansion(expansionId: Long) {
        val current = _state.value.selectedExpansionIds.toMutableSet()
        if (current.contains(expansionId)) current.remove(expansionId) else current.add(expansionId)
        _state.value = _state.value.copy(selectedExpansionIds = current)
    }

    /**
     * Inicia a partida ativa:
     * - monta listas selecionadas
     * - chama ActiveMatchController.startMatch(...)
     * - navega para a tela do cronômetro
     */
    fun startMatch(navigator: Navigator, spotifyDeviceId: String?) {
        val s = _state.value
        val gameId = s.selectedGameId ?: run {
            _state.value = _state.value.copy(error = lang.strings.value.selectAGame)
            return
        }

        if (s.selectedPlayerIds.isEmpty()) {
            _state.value = _state.value.copy(error = lang.strings.value.selectAtLeastOnePlayer)
            return
        }

        val game = s.games.firstOrNull { it.id == gameId } ?: run {
            _state.value = _state.value.copy(error = lang.strings.value.unknownError)
            return
        }

        val players = s.players.filter { s.selectedPlayerIds.contains(it.id) }
        val expansions = s.expansions.filter { s.selectedExpansionIds.contains(it.id) }

        screenModelScope.launch {
            _state.value = _state.value.copy(isLoading = true, error = null)

            // 1) Spotify é opcional: tenta tocar se der, mas NUNCA bloqueia a partida
            val spotifyUrl = game.spotifyUrl
            if (!spotifyUrl.isNullOrBlank() && !spotifyDeviceId.isNullOrBlank()) {
                // best-effort
                spotifyPlayerRepository?.play(spotifyUrl, spotifyDeviceId)
                    ?.onFailure { e ->
                        Napier.w(
                            message = "Spotify play failed (will continue match). url=$spotifyUrl deviceId=$spotifyDeviceId",
                            throwable = e
                        )
                    }
            } else {
                Napier.d(
                    "Spotify skipped. url=${spotifyUrl ?: "null"} deviceId=${spotifyDeviceId ?: "null"}"
                )
            }

            // 2) Inicia a partida ativa
            activeMatchController.startMatch(
                game = game,
                players = players,
                expansions = expansions
            )

            _state.value = _state.value.copy(isLoading = false)
            navigator.replace(ActiveMatchScreen())
        }
    }
}
